/**
 * PaintScout theme factory.
 * Mirrors the structure of @paintscout/ui's createTheme so it can be swapped
 * with a single import change when that package is published.
 *
 * Uses MUI v5's createTheme under the hood (PaintScout uses MUI v4, but the
 * theme shape is kept identical via module augmentation).
 */
import { createTheme as createMuiTheme, responsiveFontSizes } from '@mui/material/styles'
import type { ThemeOptions as MuiThemeOptions } from '@mui/material/styles'
import { brandPalette, secondaryPalette, semanticPalette } from './palette'
import type { BrandPalette, SecondaryPalette, SemanticPalette } from './palette'

export interface PaintScoutThemeExtension {
  header: {
    height: {
      sm: string
      md: string
      lg?: string
    }
  }
  fontScale: number
  palette: {
    primary: string
    dark: string
    light: string
    secondary: SecondaryPalette
    brand: BrandPalette
    semantic: SemanticPalette
  }
}

export interface PaintScoutThemeOptions {
  fontScale?: number
  palette?: {
    primary?: string
    dark?: string
    light?: string
    secondary?: SecondaryPalette
    brand?: BrandPalette
    semantic?: SemanticPalette
  }
  header?: {
    height?: {
      sm?: string
      md?: string
      lg?: string
    }
  }
}

// Module augmentation for MUI v5
declare module '@mui/material/styles' {
  interface Theme {
    paintscout: PaintScoutThemeExtension
    borderRadius: {
      none: string
      sm: string
      md: string
      lg: string
      xl: string
      '2xl': string
      '3xl': string
      full: string
    }
  }
  interface ThemeOptions {
    paintscout?: PaintScoutThemeOptions
    borderRadius?: Theme['borderRadius']
  }
}

export function createTheme(options?: MuiThemeOptions & { paintscout?: PaintScoutThemeOptions }) {
  const fontScale = options?.paintscout?.fontScale ?? 1

  const fontSizes = {
    h1: `${2.5 * fontScale}rem`,
    h2: `${1.5 * fontScale}rem`,
    h3: `${1.25 * fontScale}rem`,
    h4: `${1 * fontScale}rem`,
    h5: `${1 * fontScale}rem`,
    subtitle1: `${0.875 * fontScale}rem`,
    body1: `${1 * fontScale}rem`,
    button: `${1 * fontScale}rem`,
    caption: `${0.875 * fontScale}rem`,
    overline: `${0.875 * fontScale}rem`,
  }

  const lineHeight = 1.4
  const fontWeight = 400
  const fontWeightMedium = 500
  const fontWeightBold = 600

  const blackText = brandPalette.Neutral.P40
  const greyText = brandPalette.Neutral.P75

  const breakPointsAdjuster = 190

  const theme = createMuiTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600 + breakPointsAdjuster,
        md: 768 + breakPointsAdjuster,
        lg: 1024 + breakPointsAdjuster,
        xl: 1280 + breakPointsAdjuster,
      },
    },
    borderRadius: {
      none: '0',
      sm: '0.125rem',
      md: '0.25rem',
      lg: '0.5rem',
      xl: '0.75rem',
      '2xl': '1rem',
      '3xl': '1.5rem',
      full: '9999px',
    },
    paintscout: {
      header: {
        height: {
          sm: options?.paintscout?.header?.height?.sm ?? '50px',
          md: options?.paintscout?.header?.height?.md ?? '60px',
          lg: options?.paintscout?.header?.height?.lg ?? '90px',
        },
      },
      fontScale,
      palette: {
        primary: brandPalette.Blue.Blue55,
        dark: brandPalette.Blue.Blue50,
        light: brandPalette.Blue.Blue80,
        secondary: { ...secondaryPalette },
        brand: { ...brandPalette },
        semantic: { ...semanticPalette },
      },
    },
    typography: {
      htmlFontSize: 16,
      fontWeightMedium,
      fontFamily: 'Inter, Arial, Helvetica, sans-serif',
      h1: { fontSize: fontSizes.h1, color: blackText, fontWeight: fontWeightBold, lineHeight },
      h2: { fontSize: fontSizes.h2, color: blackText, fontWeight: fontWeightMedium, lineHeight },
      h3: { fontSize: fontSizes.h3, color: blackText, fontWeight: fontWeightMedium, lineHeight },
      h4: { fontSize: fontSizes.h4, color: blackText, lineHeight, fontWeight: fontWeightMedium },
      h5: { fontSize: fontSizes.subtitle1, color: greyText, lineHeight, fontWeight: fontWeightBold },
      h6: { fontSize: fontSizes.subtitle1, color: greyText, lineHeight, fontWeight },
      subtitle1: { fontSize: fontSizes.subtitle1, color: greyText, lineHeight, fontWeight },
      subtitle2: { fontSize: fontSizes.subtitle1, color: greyText, lineHeight, fontWeight },
      body1: { fontSize: fontSizes.body1, lineHeight, fontWeight },
      body2: { fontSize: fontSizes.body1, lineHeight, fontWeight },
      button: {
        fontFamily: 'Inter, Arial, Helvetica, sans-serif',
        fontSize: fontSizes.button,
        lineHeight,
        fontWeight: fontWeightBold,
        textTransform: 'none' as const,
      },
      caption: { fontSize: fontSizes.caption, lineHeight, fontWeight },
      overline: { fontSize: fontSizes.overline, lineHeight, fontWeight, textTransform: 'none' as const },
    },
    palette: {
      primary: { main: brandPalette.Blue.Blue55 },
      secondary: { main: '#ffffff' },
      error: {
        main: semanticPalette.Danger.S60,
        light: semanticPalette.Danger.S95,
        dark: semanticPalette.Danger.S40,
      },
      warning: {
        main: semanticPalette.Warning.S80,
        light: semanticPalette.Warning.S95,
        dark: semanticPalette.Warning.S40,
      },
      success: {
        main: semanticPalette.Success.S60,
        light: semanticPalette.Success.S95,
        dark: semanticPalette.Success.S40,
      },
      info: {
        main: semanticPalette.Info.S70,
        light: semanticPalette.Info.S95,
        dark: semanticPalette.Info.S40,
      },
      text: { primary: blackText },
      divider: brandPalette.Neutral.P90,
      grey: {
        50: brandPalette.Neutral.P98,
        100: brandPalette.Neutral.P96,
        200: brandPalette.Neutral.P94,
        300: brandPalette.Neutral.P90,
        400: brandPalette.Neutral.P80,
        500: brandPalette.Neutral.P70,
        600: brandPalette.Neutral.P60,
        700: brandPalette.Neutral.P50,
        800: brandPalette.Neutral.P35,
        900: brandPalette.Neutral.P30,
      },
      background: {
        default: brandPalette.Neutral.P98,
        paper: '#ffffff',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: brandPalette.Neutral.P98,
            '@media (pointer: fine)': {
              scrollbarGutter: 'stable',
            },
          },
          body: {
            backgroundColor: brandPalette.Neutral.P98,
          },
          // Unstyled native buttons only — do not reset MUI <Button> (MuiButtonBase-root).
          // Wrapped in :where() so specificity is 0 and any sx={{ border, background, ... }}
          // on Box component="button" wins without needing !important.
          ':where(button:not(.MuiButtonBase-root))': {
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            font: 'inherit',
          },
          strong: {
            fontWeight: fontWeightBold,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none !important',
            border: `1px solid ${brandPalette.Neutral.P90}`,
          },
          elevation0: {
            border: 'none',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingTop: 12,
            paddingLeft: 12,
            paddingRight: 12,
            paddingBottom: 12,
            color: blackText,
          },
        },
      },
    },
    ...options,
  })

  return fontScale < 1 ? theme : responsiveFontSizes(theme)
}
